<template>
  <div
    :is-loading="isLoading"
  >
    <b-row>
      <OverviewStat
        title="IMPRESSIONS"
        :value="toLocaleString(getImpressions)"
        tooltip-text="The number of times your ad was served."
        data-test="impressions"
      />
      <OverviewStat
        title="CLICKS"
        :value="toLocaleString(getClicks)"
        tooltip-text="The number of times someone clicked on your ad."
        data-test="clicks"
      />
      <OverviewStat
        title="CTR"
        tooltip-text="The percentage of impressions that resulted in a click."
        data-test="ctr"
      >
        <template #custom-value>
          {{ toLocaleString(parseFloat(getCTR), true) }} <small v-if="getCTR!=='-'">%</small>
        </template>
      </OverviewStat>
      <OverviewStat
        title="SCROLLS"
        :value="toLocaleString(getScrolls)"
        tooltip-text="The number of times someone scrolled on your ad."
        data-test="scrolls"
        v-if="getScrollsDisplayed"
      />
      <OverviewStat
        title="STR"
        tooltip-text="The percentage of impressions that resulted in a scroll."
        data-test="ctr"
        v-if="getScrollsDisplayed"
      >
        <template #custom-value>
          {{ toLocaleString(parseFloat(getSTR), true) }} <small v-if="getSTR!=='-'">%</small>
        </template>
      </OverviewStat>
      <OverviewStat
        title="COST"
        tooltip-text="The total amount spent on serving ads across publishers."
        data-test="cost"
      >
        <template #custom-value>
          <span v-html="formattedMoneyAmount(parseFloat(getSpend))" />
        </template>
      </OverviewStat>
      <OverviewStat
        title="CPM"
        tooltip-text="The average cost of 1,000 impressions."
        data-test="cpm"
      >
        <template #custom-value>
          <span v-html="formattedMoneyAmount(parseFloat(getCPM))" />
        </template>
      </OverviewStat>
      <OverviewStat
        title="CPC"
        tooltip-text="The average cost of each click."
        data-test="cpc"
      >
        <template #custom-value>
          <span v-html="formattedMoneyAmount(parseFloat(getCPC))" />
        </template>
      </OverviewStat>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import currenciesMixin from '@/core/mixins/currencies';
import numbersHelper from '@/core/mixins/numbersHelper';
import OverviewStat from './OverviewStat.vue';


export default {
  name: 'CampaignOverviewStats',
  components: {
    OverviewStat,
  },
  mixins: [
    currenciesMixin,
    numbersHelper,
  ],
  props: {
    deliveryList: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters('campaigns', [
      campaignTypes.GET_ORDERS_CURRENCY,
    ]),
    ...mapState('campaigns', [
      'currencies',
      'order',
    ]),
    getScrollsDisplayed() {
      return this.deliveryList.some((delivery) => delivery.scrolls != null);
    },
    getImpressions() {
      return this.getEventsAmount('impressions');
    },
    getClicks() {
      return this.getEventsAmount('clicks');
    },
    getScrolls() {
      return this.getEventsAmount('scrolls');
    },
    getScrollableImpressions() {
      return this.getEventsAmount('scrollableImpressions');
    },
    getSpend() {
      return this.getEventsAmount('cost');
    },
    getCTR() {
      return this.getEventRate(this.getClicks, this.getImpressions);
    },
    getSTR() {
      return this.getEventRate(this.getScrolls, this.getScrollableImpressions);
    },
    getCPM() {
      return this.getSpend / (this.getImpressions / 1000);
    },
    getCPC() {
      if (!this.getClicks) return 0;
      return this.getSpend / this.getClicks;
    },
  },
  methods: {
    getEventsAmount(name) {
      if (this.deliveryList.length === 0) return '-';
      const { deliveryList } = this;
      return Object.keys(deliveryList)
        .map((key) => deliveryList[key][name])
        .filter((eventsAmount) => eventsAmount)
        .reduce((amount1, amount2) => amount1 + amount2, 0);
    },
    getEventRate(eventsAmount, impressionsAmount) {
      if (this.deliveryList.length === 0 || !eventsAmount || !impressionsAmount) return '-';
      return (eventsAmount) / (impressionsAmount) * 100;
    },
    formattedMoneyAmount(budget) {
      if (!budget) return '-';
      const { symbolString } = this.currencies
        .find((currency) => currency.id === this.GET_ORDERS_CURRENCY);
      return this.getFormattedMoneyAmount(budget.toFixed(2), symbolString, true);
    },
  },
  created() {
    this.isLoading = true;
  },
};
</script>
